import * as R from 'ramda'
import * as ReactRedux from 'react-redux'
import * as Urql from 'urql'
import * as ReactRouter from 'react-router-dom'
import * as React from 'react'

import * as common from '@rushplay/common'
import * as Analytics from '@rushplay/analytics'
import { useI18n } from '@rushplay/i18n'

import * as Session from './session'
import { Button } from './button'
import { getSafeUpdateQuery } from './use-safe-update-query'
import { useGameInfoUrlQuery } from './use-game-info-url-query'

const getGame = `
  query Game($id: ID!) {
    game(id: $id) {
      blitzEnabled
      id
      key
      loginRequired
    }
  }
`

export function GameInfoModeSelector() {
  const i18n = useI18n()
  const dispatch = ReactRedux.useDispatch()
  const { gameId } = ReactRouter.useParams()
  const [response] = Urql.useQuery({
    query: getGame,
    variables: { id: gameId },
  })
  const history = ReactRouter.useHistory()
  const location = ReactRouter.useLocation()
  const gameInfoQuery = useGameInfoUrlQuery()
  const authenticated = ReactRedux.useSelector(state =>
    Session.isAuthenticated(state.session)
  )

  const game = R.pathOr({}, ['game'], response.data)

  const isLiveCasinoGame =
    R.includes('live-casino', game.key || '') ||
    R.equals('live-casino', gameInfoQuery.referrer)

  function handleGameStart(mode) {
    history.push(`?${getSafeUpdateQuery(location, { mode })}`)
  }

  function handleLogin() {
    history.push(`?${getSafeUpdateQuery(location, { login: 'me' })}`)
  }

  if (authenticated) {
    if (isLiveCasinoGame) {
      return (
        <common.Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
        >
          <Button
            stretch
            variant="primary"
            onClick={() => handleGameStart('classic')}
            data-testid="game-info.play-classic"
          >
            {i18n.translate('game-info.play')}
          </Button>
        </common.Box>
      )
    }

    if (game.blitzEnabled) {
      return (
        <common.Box display="flex" flexDirection="column" textAlign="center">
          <common.Box
            pb={1}
            fontWeight="bold"
            textShadow="1px 1px 3px rgba(0, 0, 0, 0.5)"
          >
            {i18n.translate('game-info.choose-mode')}
          </common.Box>
          <common.Box display="flex" minWidth="280px">
            <common.Box flexGrow="1" flexShrink="1" flexBasis="0" pr={0}>
              <Button
                stretch
                onClick={() => handleGameStart('blitz')}
                data-testid="game-info.play-blitz"
              >
                {i18n.translate('game-info.blitz')}
              </Button>
            </common.Box>
            <common.Box flexGrow="1" flexShrink="1" flexBasis="0" pl={0}>
              <Button
                stretch
                variant="primary"
                onClick={() => handleGameStart('classic')}
              >
                {i18n.translate('game-info.classic')}
              </Button>
            </common.Box>
          </common.Box>
        </common.Box>
      )
    }

    return (
      <common.Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
      >
        <Button
          stretch
          variant="primary"
          onClick={() => handleGameStart('classic')}
        >
          {i18n.translate('game-info.play')}
        </Button>
      </common.Box>
    )
  } else {
    if (isLiveCasinoGame) {
      return (
        <Button onClick={handleLogin}>
          <common.Box textShadow="1px 1px 3px rgba(0, 0, 0, 0.5)">
            <common.Text
              fontWeight="bold"
              fontSize={1}
              textDecoration="underline"
            >
              {i18n.translate('game-info.live-casino-unavailable')}
            </common.Text>
          </common.Box>
        </Button>
      )
    }

    if (game.loginRequired) {
      return (
        <ReactRouter.Link to="/login">
          <common.Box textShadow="1px 1px 3px rgba(0, 0, 0, 0.5)">
            <common.Text
              fontWeight="bold"
              fontSize={1}
              textDecoration="underline"
            >
              {i18n.translate('game-info.login-required')}
            </common.Text>
          </common.Box>
        </ReactRouter.Link>
      )
    }

    if (game.blitzEnabled) {
      return (
        <common.Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
        >
          <Button
            stretch
            variant="primary"
            onClick={() => {
              dispatch(
                Analytics.playForFun({
                  title: game.key,
                  logged: authenticated,
                })
              )
              return handleGameStart('classic')
            }}
          >
            {i18n.translate('game-info.play-for-fun')}
          </Button>
          <common.Box textShadow="1px 1px 3px rgba(0, 0, 0, 0.5)" pt={1}>
            <ReactRouter.Link to="?login=me">
              <common.Text
                fontWeight="bold"
                fontSize={1}
                textDecoration="underline"
              >
                {i18n.translate('game-info.blitz-unavailable')}
              </common.Text>
            </ReactRouter.Link>
          </common.Box>
        </common.Box>
      )
    }

    return (
      <common.Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
      >
        <Button
          stretch
          variant="primary"
          onClick={() => {
            dispatch(
              Analytics.playForFun({
                title: game.key,
                logged: authenticated,
              })
            )
            return handleGameStart('classic')
          }}
        >
          {i18n.translate('game-info.play-for-fun')}
        </Button>
      </common.Box>
    )
  }
}
